import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Divider
} from '@mui/material';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
export interface QueuedSimulation {
  id: string;
  scenarioName: string;
  timePreset: 'fast' | 'balanced' | 'thorough';
  fleetPreset: 'auto' | 'savvy' | 'platinum';
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  error?: string;
  result?: any;
  ridesCount: number;
  vehiclesCount: number;
}

interface SimulationQueueProps {
  queue: QueuedSimulation[];
  onRemoveSimulation: (id: string) => void;
  onCancelSimulation: (id: string) => void;
  onRunQueue: () => void;
  onClearCompleted: () => void;
}

const getTimePresetDescription = (preset: string, t: (key: string) => string): string => {
  switch (preset) {
    case 'fast':
      return t('setup.queue.timePresets.fast');
    case 'balanced':
      return t('setup.queue.timePresets.balanced');
    case 'thorough':
      return t('setup.queue.timePresets.thorough');
    default:
      return preset;
  }
};

const getTimePresetColor = (preset: 'fast' | 'balanced' | 'thorough') => {
  switch (preset) {
    case 'fast':
      return 'success';
    case 'balanced':
      return 'default';
    case 'thorough':
      return 'primary';
    default:
      return 'default';
  }
};

const getFleetPresetColor = (preset: 'auto' | 'savvy' | 'platinum') => {
  switch (preset) {
    case 'auto':
      return 'default';
    case 'savvy':
      return 'success';
    case 'platinum':
      return 'primary';
    default:
      return 'default';
  }
};

const getFleetPresetDescription = (preset: string, t: (key: string) => string): string => {
  switch (preset) {
    case 'auto':
      return t('setup.queue.fleetPresets.auto');
    case 'savvy':
      return t('setup.queue.fleetPresets.savvy');
    case 'platinum':
      return t('setup.queue.fleetPresets.platinum');
    default:
      return preset;
  }
};

export const SimulationQueue: React.FC<SimulationQueueProps> = ({
  queue,
  onRemoveSimulation,
  onCancelSimulation,
  onRunQueue,
  onClearCompleted
}) => {
  const { t } = useTranslation('simulation');
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'queued':
        return 'default';
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'queued':
        return <Icon size={20} name="clock" />;
      case 'running':
        return <Icon size={20} name="play" />;
      case 'completed':
        return <Icon size={20} name="circle-check" />;
      case 'failed':
        return <Icon size={20} name="circle-exclamation" />;
      case 'cancelled':
        return <Icon size={20} d={ICON_FALLBACKS.Stop} />;
      default:
        return <Icon size={20} d={ICON_FALLBACKS.Queue} />;
    }
  };

  const runningSimulation = queue.find(sim => sim.status === 'running');
  const queuedSimulations = queue.filter(sim => sim.status === 'queued');
  const completedSimulations = queue.filter(sim => sim.status === 'completed');

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {t('setup.queue.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={t('setup.queue.totalChip', { count: queue.length })}
              size="small"
              variant="outlined"
            />
            <Chip
              label={t('setup.queue.queuedChip', { count: queuedSimulations.length })}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        {queue.length === 0 ? (
          <Alert severity="info">
            {t('setup.queue.empty')}
          </Alert>
        ) : (
          <>
            {/* Running Simulation */}
            {runningSimulation && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('setup.queue.currentlyRunning')}
                </Typography>
                <Card variant="outlined" sx={{ bgcolor: 'grey.100' }}>
                  <CardContent sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(runningSimulation.status)}
                        <Typography variant="body2" fontWeight="bold">
                          {runningSimulation.scenarioName}
                        </Typography>
                        <Chip
                          label={getFleetPresetDescription(runningSimulation.fleetPreset, t)}
                          size="small"
                          color={
                            runningSimulation.fleetPreset === 'auto' ? 'default' :
                            runningSimulation.fleetPreset === 'savvy' ? 'success' : 'primary'
                          }
                        />
                        <Chip
                          label={getTimePresetDescription(runningSimulation.timePreset, t)}
                          size="small"
                          color={getTimePresetColor(runningSimulation.timePreset)}
                          variant="outlined"
                        />
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => onCancelSimulation(runningSimulation.id)}
                        color="error"
                      >
                        <Icon size={20} d={ICON_FALLBACKS.Stop} />
                      </IconButton>
                    </Box>
                    {runningSimulation.progress !== undefined && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={runningSimulation.progress} 
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t('setup.queue.percentComplete', { percent: runningSimulation.progress })}
                        </Typography>
                      </Box>
                    )}
                    {runningSimulation.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {runningSimulation.error}
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Queue Controls */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<Icon size={20} name="play" />}
                onClick={onRunQueue}
                disabled={queuedSimulations.length === 0 || !!runningSimulation}
                size="small"
              >
                {t('setup.queue.runQueue', { count: queuedSimulations.length })}
              </Button>
              {completedSimulations.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={onClearCompleted}
                  size="small"
                >
                  {t('setup.queue.clearCompleted', { count: completedSimulations.length })}
                </Button>
              )}
            </Box>

            {/* Queue List */}
            <List dense>
              {queue.map((simulation, index) => (
                <React.Fragment key={simulation.id}>
                  <ListItem 
                    sx={{ 
                      bgcolor: simulation.status === 'completed' ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                      borderRadius: simulation.status === 'completed' ? 1 : 0,
                      mb: simulation.status === 'completed' ? 0.5 : 0
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(simulation.status)}
                          <Typography variant="body2" fontWeight="bold">
                            {simulation.scenarioName}
                          </Typography>
                          <Chip
                            label={getFleetPresetDescription(simulation.fleetPreset, t)}
                            size="small"
                            color={
                              simulation.fleetPreset === 'auto' ? 'default' :
                              simulation.fleetPreset === 'savvy' ? 'success' : 'primary'
                            }
                          />
                          <Chip
                            label={getTimePresetDescription(simulation.timePreset, t)}
                            size="small"
                            color={getTimePresetColor(simulation.timePreset)}
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('setup.queue.ridesAndVehicles', { rides: simulation.ridesCount, vehicles: simulation.vehiclesCount })}
                          </Typography>
                          {simulation.status === 'running' && simulation.progress !== undefined && (
                            <Typography variant="caption" color="primary">
                              {t('setup.queue.percentComplete', { percent: simulation.progress })}
                            </Typography>
                          )}
                          {simulation.status === 'failed' && simulation.error && (
                            <Typography variant="caption" color="error">
                              {t('setup.queue.errorLabel', { message: simulation.error })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        size="small" 
                        onClick={() => onRemoveSimulation(simulation.id)}
                        disabled={simulation.status === 'running'}
                      >
                        <Icon size={20} name="trash" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < queue.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Progress Summary */}
            {runningSimulation && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('setup.queue.summary', { name: runningSimulation.scenarioName, remaining: queuedSimulations.length, completed: completedSimulations.length })}
                </Typography>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
